import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getHealth, getTaskStatus, getFormStatistics } from '../services/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [healthData, setHealthData] = useState<any>(null);
  const [taskData, setTaskData] = useState<any>(null);
  const [formStats, setFormStats] = useState<any>(null);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all data with individual error handling
      const [health, tasks, forms] = await Promise.all([
        getHealth().catch(err => {
          console.error('Health API error:', err);
          return { data: null };
        }),
        getTaskStatus().catch(err => {
          console.error('Task API error:', err);
          return { data: null };
        }),
        getFormStatistics().catch(err => {
          console.error('Forms API error:', err);
          return { data: null };
        }),
      ]);

      setHealthData(health.data);
      setTaskData(tasks.data?.status || tasks.data);
      setFormStats(forms.data?.statistics || forms.data);

      setError(null);
    } catch (err: any) {
      console.error('Dashboard error:', err);
      setError(err.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !healthData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !healthData) {
    return <Alert severity="error">{error}</Alert>;
  }

  const taskChartData = taskData ? [
    { name: 'Pending', value: taskData.pending || 0 },
    { name: 'Claimed', value: taskData.claimed || 0 },
    { name: 'In Progress', value: taskData.inProgress || 0 },
    { name: 'Completed', value: taskData.completed || 0 },
    { name: 'Failed', value: taskData.failed || 0 },
  ] : [];

  const formChartData = formStats?.byStatus ? Object.entries(formStats.byStatus).map(([status, count]) => ({
    name: status,
    value: count,
  })) : [];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Workflow Management Dashboard
      </Typography>

      {/* Status Cards Row */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: 2,
        mb: 3
      }}>
        {/* System Health */}
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              System Status
            </Typography>
            <Typography variant="h5">
              {healthData?.status === 'healthy' ? (
                <Chip icon={<CheckCircleIcon />} label="Healthy" color="success" />
              ) : (
                <Chip icon={<ErrorIcon />} label="Unhealthy" color="error" />
              )}
            </Typography>
            <Stack spacing={0.5} mt={1}>
              <Typography variant="body2" color="textSecondary">
                Database: {healthData?.services?.database ? '✓' : '✗'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Redis: {healthData?.services?.redis ? '✓' : '✗'}
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        {/* Task Summary */}
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Human Tasks
            </Typography>
            <Typography variant="h5">
              {taskData?.pending || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Pending Tasks
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              In Progress: {taskData?.inProgress || 0}
            </Typography>
          </CardContent>
        </Card>

        {/* Form Submissions */}
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Form Submissions
            </Typography>
            <Typography variant="h5">
              {formStats?.total || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Submissions
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Success Rate: {formStats?.successRate || '0%'}
            </Typography>
          </CardContent>
        </Card>

        {/* Total Tasks */}
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Tasks
            </Typography>
            <Typography variant="h5">
              {(taskData?.total || 0)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              All Time
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Completed: {taskData?.completed || 0}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Charts Row */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
        gap: 2,
        mb: 3
      }}>
        {/* Task Status Chart */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Task Status Distribution
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={taskChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {taskChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Paper>

        {/* Form Submission Status */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Form Submission Status
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={formChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      {/* System Information */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          System Information
        </Typography>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 2
        }}>
          <Box>
            <Typography variant="body2" color="textSecondary">
              Environment: {healthData?.environment || 'N/A'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">
              Version: {healthData?.version || 'N/A'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">
              Last Updated: {new Date(healthData?.timestamp || Date.now()).toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Dashboard;